import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function BillingSuccessPage() {
  const { user, loading, setUser } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, attempt: 0 });
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    let attempt = 0;
    const tick = async () => {
      attempt += 1;
      try {
        const { data } = await api.get(`/stripe/checkout/status/${sessionId}`);
        setState({ status: data.status, payment_status: data.payment_status, attempt });
        if (data.user) setUser(data.user);
        if (data.status === "complete" || data.payment_status === "paid" || data.status === "expired") {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch (e) {
        setState((s) => ({ ...s, status: "error", attempt }));
        if (pollRef.current) clearInterval(pollRef.current);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionId, setUser]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;

  const paid = state.payment_status === "paid" || state.status === "complete";
  const expired = state.status === "expired";

  return (
    <DashboardShell>
      <div className="max-w-xl mx-auto text-center py-16" data-testid="billing-success-page">
        {!sessionId && (
          <div className="text-[#A0A0A0]">No checkout session in URL.</div>
        )}
        {sessionId && !paid && !expired && (
          <>
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-6" />
            <h1 className="font-display text-4xl font-extrabold tracking-tighter mb-3">Confirming your subscription…</h1>
            <p className="text-[#A0A0A0]">This usually takes a few seconds. (Attempt {state.attempt})</p>
          </>
        )}
        {paid && (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.4} />
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mb-4" data-testid="billing-success-title">
              You're subscribed!
            </h1>
            <p className="text-[#A0A0A0] mb-8">Welcome to {user.subscription_tier ? user.subscription_tier.toUpperCase() : ""}. Your full plan is unlocked.</p>
            <div className="flex gap-3 justify-center">
              <Link to="/dashboard" className="bg-[#D4AF37] text-[#080808] font-bold px-6 py-3 hover:bg-[#F0C84B] transition-colors" data-testid="billing-success-dashboard">
                Go to Dashboard
              </Link>
              <Link to="/generate/video" className="border border-[#2A2A2A] hover:border-[#D4AF37] px-6 py-3 transition-colors">
                Start Creating
              </Link>
            </div>
          </>
        )}
        {expired && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" strokeWidth={1.4} />
            <h1 className="font-display text-3xl font-extrabold tracking-tighter mb-3">Checkout expired</h1>
            <p className="text-[#A0A0A0] mb-8">No charge was made. Try again from the pricing page.</p>
            <Link to="/pricing" className="bg-[#D4AF37] text-[#080808] font-bold px-6 py-3 hover:bg-[#F0C84B] transition-colors">Back to pricing</Link>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
